<template>
  <ClientOnly>
    <div
      class="etx-container Container"
      :class="[isEditing ? 'etx-editing' : '', ...paragraphClassList]"
      v-if="highlightModuleDataSource?.data?.value?.length > 0 || isEditing"
    >
      <div class="etx-center Center">
        <!-- <LoaderSwiper /> -->
        <HighlightComponent
          :background="variant"
          :dataSource="highlightModuleDataSource"
          @reference-clicked="handleHighlightReferenceClicked"
          :allRefsText="labelAllReferences"
          @all-references-clicked="handleHighlightAllReferencesClicked"
        />
      </div>
    </div>
  </ClientOnly>
</template>

<script setup lang="ts">
import {
  HighlightComponent,
  LosysApiService,
  ReactiveAsyncDataSource,
  type HighlightEntry,
  type LosysApiAuthResult,
  type LosysApiProjectFilter,
} from '@etxdev/losys-lib'
import type { integer } from '@elastic/elasticsearch/lib/api/types'
import {
  APPROVAL_BY_MARKETING_IDS,
  IS_HIGHLIGHT_IDS,
  EQUANS_COMPANIES_IDS,
  KUMA_COMPANIES_IDS,
} from '~/composables/useCompetencyTaxonomy'

const tenantId = useTenant()
const { data: competencies } = await useAsyncData(
  `getCompetencyTermsLosys-${tenantId}`,
  async () => {
    const result = await useGraphqlQuery('getCompetencyTermsLosys', {
      vid: tenantId === 'equans' ? 'competencies' : 'competencies_kuma',
    })
    const items = result.data?.entityQuery?.items ?? []
    return items
  },
)

const currentLanguage = useCurrentLanguage().value || 'de'
const initData: Ref<InitData> = await useInitData()
const { globalConfig } = initData.value
const { referencesPage } = globalConfig
const referencesPageUrl = referencesPage?.uri?.path || '/'
const nuxtRoute = useRoute()
const currentPath = nuxtRoute.path

const { $texts } = useNuxtApp()
const labelAllReferences = computed(() => {
  if (
    new URL(referencesPageUrl, window.location.origin).pathname === currentPath
  ) {
    return undefined!
  }
  return $texts('losys.slider_all_references', 'Alle Referenzen anzeigen')
})

const props = defineProps<{
  id: string | integer
  parentId: string
}>()

const { options, isEditing } = defineBlokkli({
  bundle: 'references_slider',
  globalOptions: ['spacingTop', 'spacingBottom'],
  options: {
    variant: {
      type: 'radios',
      label: 'Ausrichtung',
      default: 'left',
      options: {
        left: 'Link',
        right: 'Rechts',
      },
    },
  },
  editor: {
    addBehaviour: 'no-form',
    disableEdit: true,
    maxInstances: 1,
  },
})

const paragraphClassList = computed(() => {
  const classList = []

  if (options.value.spacingTop) {
    classList.push(options.value.spacingTop)
  }

  if (options.value.spacingBottom) {
    classList.push(options.value.spacingBottom)
  }

  return classList
})

const variant = computed(() => {
  if (options.value.variant) {
    return options.value.variant
  }
  return 'left'
})

/**
 * init losys api service
 * use etrex.dev proxy for now, which injects client secret into request
 * Setting the authStore param is not needed. Default behavior: token is stored within service instance.
 * If you want to store data elsewhere (as in example) pass a authStore impl.
 */
const apiService: LosysApiService = new LosysApiService({
  authEndpoint:
    'https://equans.etrex.live/middleware-losys/proxy.php?endpoint=auth&target=https://api.referenz-verwaltung.ch/oauth/token',
  apiHost: 'https://api.referenz-verwaltung.ch/api',
  authStore: {
    set: (data: LosysApiAuthResult) => {
      localStorage.setItem(
        'losys-auth',
        JSON.stringify({
          access_token: data.access_token,
          company: undefined!, // dont need it
        }),
      )
    },
    get: (): LosysApiAuthResult | undefined => {
      const storedData = localStorage.getItem('losys-auth')
      if (!storedData) return undefined

      return JSON.parse(storedData) as LosysApiAuthResult
    },
  },
})

if (import.meta.client) {
  apiService.authenticateAndGetBasicInformation().then(() => {
    loadData()
  })
}

const pageSize: number = 100 // default page size for data sources
const resultSize: number = 3 // Client side page size for results

const contextualCompaniesIds =
  tenantId === 'kuma'
    ? KUMA_COMPANIES_IDS.map(String)
    : EQUANS_COMPANIES_IDS.map(String)

/**
 * Highlight data source
 * Data source for the highlight component
 *
 */
const highlightModuleDataSource: ReactiveAsyncDataSource<
  HighlightEntry,
  LosysApiProjectFilter
> = new ReactiveAsyncDataSource<HighlightEntry, LosysApiProjectFilter>(
  (filter: LosysApiProjectFilter): Promise<HighlightEntry[]> => {
    return new Promise<HighlightEntry[]>((resolve, _reject) => {
      // fetch data from losys api

      apiService
        .getCustomerProjects({
          ...filter,
          expand: [
            'project_images',
            'project_categories',
            'project_properties',
          ],
          languages: [currentLanguage],
          categoryIds: getLosysCategoriesByNid(
            props.parentId,
            competencies.value,
          ),
          companyIds: contextualCompaniesIds,
        })
        .then((restData) => {
          // Filter by highlighted and approved projects
          const approvedData = restData
            .filter((y) => {
              const props = Object.values(y.project_properties)
              const isHighlight = props.find((y) =>
                IS_HIGHLIGHT_IDS.includes(y.projectAttributeId),
              )
              return isHighlight?.value === 'True'
            })
            .filter((x) => {
              const props = Object.values(x.project_properties)
              const approvalByMarketing = props.find((x) =>
                APPROVAL_BY_MARKETING_IDS.includes(x.projectAttributeId),
              )
              return approvalByMarketing?.value === 'True'
            })
            // Remove duplicate results by id
            .filter(
              (item, index, self) => self.findIndex(i => i.id === item.id) === index
            )
            .sort(() => Math.random() - 0.5)
            .slice(0, resultSize)

          const data: HighlightEntry[] = approvedData.map((x) => {
            let imageKitUrl = (
              x.project_images ? x.project_images[0]?.extralarge_image : ''
            ).replace(
              'https://api.referenz-verwaltung.ch/static/images/project/',
              'https://ik.imagekit.io/equans/losys/',
            )
            imageKitUrl = `${imageKitUrl}?tr=w-3000,ar-3-2,fo-auto`

            return {
              id: x.id,
              image: imageKitUrl,
              title: {
                main: x.title,
                sub: x.city,
              },
            }
          })

          resolve(data)
        })
    })
  },
)

setTimeout(() => {
  highlightModuleDataSource.setData(highlightModuleDataSource?.data?.value)
}, 3000)

/**
 * Trigged if an individual reference is clicked within highlight module
 */
const handleHighlightReferenceClicked = (slide: any) => {
  if (slide?.id) {
    window.location.href = `/${currentLanguage}/project/${slide?.id}`
  }
}

/**
 * Trigged if all references is clicked within highlight module
 */
const handleHighlightAllReferencesClicked = () => {
  window.location.href = referencesPageUrl
}

const loadData = () => {
  // initial load for highlight module data source
  highlightModuleDataSource.load(
    {
      pageIndex: 0,
      pageSize: pageSize,
      expand: ['project_images'],
    },
    'overwrite',
  )
}
</script>
